import { fonts } from 'unocssconfig/theme.mjs'

import { FILTER_COLORS } from '@/constants'
import type { FilterTagId, LocaleCarouselImage, LocaleUrl, NewsTagColor } from '@/types'

export function formatDate (datestring: string, locale: Intl.LocalesArgument = 'en', options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' }) {
  if (!datestring) return
  const date = new Date(datestring)
  return date.toLocaleDateString(locale, options)
}

export function getAssetURL (filepath: string): string {
  return new URL(`../assets/${filepath}`, import.meta.url).href
}

export function groupBy (data: Array<object>, key: string, options?: { fallbackGroup?: string | false, transformer?: (value: object) => unknown }) {
  const {
    fallbackGroup = 'default',
    transformer = (value: object) => value
  } = options || {}

  return data.reduce((acc, item) => {
    const group = typeof item[key] === 'string' ? item[key] : fallbackGroup
    if (group) {
      if (!acc[group]) acc[group] = []
      acc[group].push(transformer(item))
    }
    return acc
  }, {})
}

export function sleep (time: number) {
  return new Promise(resolve => setTimeout(resolve, time))
}

export async function processPromises<T = unknown> (promises: Promise<T>[]): Promise<{ fulfilled: T[], rejected: Error[] }> {
  const results = await Promise.allSettled(promises)
  return results.reduce((acc, res) => {
    if ('reason' in res) {
      acc.rejected.push(res.reason)
    } else {
      acc.fulfilled.push(res.value)
    }
    return acc
  }, { fulfilled: [] as T[], rejected: [] as Error[] })
}

export function memoize (fn) {
  if (typeof fn !== 'function') {
    throw new Error('Memoization not allowed: the argument must be a function')
  }

  const cache = {}

  return function (...args) {
    const key = JSON.stringify(args)

    if (!cache.hasOwnProperty(key)) { // eslint-disable-line
      cache[key] = fn.apply(this, args)
    }

    return cache[key]
  }
}

export function fontsLoad (_fonts = fonts, size = '1em') {
  return Promise.all(_fonts.map(font => {
    const weight = font.weight || 400
    return document.fonts.load(`${weight} ${size} ${font.name}`)
  }))
}

/**
 * Helper to create a carousel image with locale support.
 * @param localePath - The locale-agnostic path (e.g., 'red/red_c1_s1.jpg')
 * @param alt - Alt text for the image
 */
export function carouselImg (localePath: string, alt: string): LocaleCarouselImage {
  return { src: '', localePath, alt }
}

export function desaturateColor (hex: string, amount = 0.3): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  const gray = Math.round((r + g + b) / 3)
  const newR = Math.round(r + (gray - r) * amount)
  const newG = Math.round(g + (gray - g) * amount)
  const newB = Math.round(b + (gray - b) * amount)
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`
}

// ============================================
// Color utilities for News/Filter system
// ============================================

/**
 * Get the color scheme for a filter tag - single source of truth
 */
export function getTagColor (tagId: FilterTagId): NewsTagColor {
  return FILTER_COLORS[tagId]
}

/**
 * Resolve a locale-aware URL to the appropriate string for the current locale.
 * @param url - Either a simple string URL or a LocaleUrl object with `it` and `en` keys
 * @param locale - The current locale ('it' or 'en')
 * @returns The resolved URL string
 */
export function resolveLocaleUrl (url: string | LocaleUrl, locale: 'it' | 'en'): string {
  if (typeof url === 'string') {
    return url
  }
  return url[locale] || url.it
}
